import React, {
	RefAttributes,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem from "../../../../../../services/PaymentSystem";
import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../redux/store";
import getCompanies from "../../../../../../redux/services/Company/getCompanies";
import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import LabeledField from "../../../../../../components/LabeledField";
import { useTaxiServiceFilterAccessControl } from "../../../../../../hooks";
import translationPath from "../../constants/translationPath";

import InternalController from "./Controller";

const CompanyAndTaxiServiceBase = react.withController<
	CompanyAndTaxiService.PropsBase,
	CompanyAndTaxiService.Controller
>(({ value, onChange, controller }) => {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();
	const { subCompaniesOptions, subTaxiServiceOptions } =
		useTaxiServiceFilterAccessControl<any>();

	const companies = useTypedSelector(
		(state) => state.ordersPageReducer.companies,
	);

	const valueEditor = useObjectEditor(value, onChange);

	const taxiServiceIds = valueEditor.useGetter("taxiServiceIds");
	const setTaxiServiceIds = valueEditor.useSetter("taxiServiceIds");

	const selectedCompanies = valueEditor.useGetter("companyIds");
	const setSelectedCompanies = valueEditor.useSetter("companyIds");

	const [initialCompanyIds] = useState(selectedCompanies);
	const [initialTaxiServiceIds] = useState(taxiServiceIds);
	const [error, setError] = useState(false);

	useEffect(() => {
		dispatch(getCompanies());
	}, [dispatch]);

	const companiesOptions = useMemo(
		() => subCompaniesOptions({ initialCompanyIds }),
		[subCompaniesOptions, initialCompanyIds],
	);

	const taxiServiceOptions = useMemo(
		() =>
			subTaxiServiceOptions({
				initialTaxiServiceIds,
				initialCompanyIds,
				selectedCompanies,
			}),
		[
			subTaxiServiceOptions,
			initialTaxiServiceIds,
			initialCompanyIds,
			selectedCompanies,
		],
	);

	const selectedCompanyId = selectedCompanies[0] ?? null;
	const selectedTaxiServiceId = taxiServiceIds[0] ?? null;

	const updateCompany = useCallback(
		(companyId: Select.Value) => {
			const id = typeof companyId === "number" ? companyId : null;
			setSelectedCompanies(id == null ? [] : [id]);
			setTaxiServiceIds([]);
			setError(false);
		},
		[setSelectedCompanies, setTaxiServiceIds],
	);

	const updateTaxiService = useCallback(
		(taxiServiceId: Select.Value) => {
			const id = typeof taxiServiceId === "number" ? taxiServiceId : null;
			setTaxiServiceIds(id == null ? [] : [id]);
			setError(false);
		},
		[setTaxiServiceIds],
	);

	useEffect(() => {
		if (
			companiesOptions.length === 1 &&
			selectedCompanies.length === 0
		) {
			updateCompany(companiesOptions[0].key);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companiesOptions]);

	useEffect(() => {
		if (
			taxiServiceOptions.length === 1 &&
			taxiServiceIds.length === 0 &&
			selectedCompanies.length === 1
		) {
			updateTaxiService(taxiServiceOptions[0].key);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServiceOptions, selectedCompanies.length]);

	useEffect(() => {
		if (!selectedTaxiServiceId || !companies.items?.length) return;

		const stillValid = companies.items.some(
			(company) =>
				selectedCompanies.includes(company.id) &&
				(company.taxiServices ?? []).some(
					(service) => service.id === selectedTaxiServiceId,
				),
		);

		if (!stillValid) setTaxiServiceIds([]);
	}, [
		companies.items,
		selectedCompanies,
		selectedTaxiServiceId,
		setTaxiServiceIds,
	]);

	controller.setContext({ value, setError });

	return (
		<>
			<LabeledField
				label={t(`${translationPath}.modal.company.title`) || ""}
			>
				<Select
					disabled={companiesOptions.length === 1}
					placeholder={
						t(`${translationPath}.modal.company.chooseCompany`) ||
						""
					}
					error={error}
					value={selectedCompanyId}
					options={companiesOptions}
					onChange={updateCompany}
				/>
			</LabeledField>
			<LabeledField
				label={t(`${translationPath}.modal.branch.title`) || ""}
			>
				<Select
					disabled={
						selectedCompanies.length === 0 ||
						taxiServiceOptions.length <= 1
					}
					placeholder={
						t(`${translationPath}.modal.branch.chooseBranch`) || ""
					}
					error={error}
					value={selectedTaxiServiceId}
					options={
						taxiServiceOptions.length === 0
							? undefined
							: taxiServiceOptions
					}
					onChange={updateTaxiService}
				/>
			</LabeledField>
		</>
	);
}, InternalController);
const CompanyAndTaxiService = memo(CompanyAndTaxiServiceBase);

declare namespace CompanyAndTaxiService {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: (x: Value) => void;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value {
		taxiServices: PaymentSystem.Model["taxiServices"];
		taxiServiceIds: PaymentSystem.Model["taxiServiceIds"];
		companies: PaymentSystem.Model["companies"];
		companyIds: PaymentSystem.Model["companyIds"];
	}
}

export default CompanyAndTaxiService;
